import { Icon } from '../../Icon'
import { mapPathForLocation } from '../resourcesData'

// Station Maps tab — prototype L3731-3744 visual shape, backed by the
// real PNG/JPG asset in /public/station-maps/. The prototype's pannable
// canvas with absolute-positioned pins is a mock; we don't have the
// data to reproduce it, and a real station map is more useful anyway.
//
// `event.location` is free-text NVARCHAR(255); mapPathForLocation() does
// a case-insensitive substring match against the known station set (see
// resourcesData.ts STATION_SLUGS / manifest.json). Unmatched → empty
// state explaining the gap.

interface Props {
  location: string | null
}

export function StationMapsTab({ location }: Props) {
  const hit = mapPathForLocation(location)

  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-2">Station Maps</h3>
      <p className="text-sm text-ink-soft mb-4">
        {hit
          ? `${hit.label} — duty stations and key landmarks.`
          : location
            ? `No detailed map available for “${location}” yet.`
            : 'No station set for this event yet.'}
      </p>

      {hit ? (
        <figure className="rounded-lg overflow-hidden border border-line bg-white">
          <img
            src={hit.imagePath}
            alt={`${hit.label} station map`}
            className="block w-full"
            style={{ aspectRatio: '16 / 9', objectFit: 'cover' }}
          />
          <figcaption className="text-xs text-ink-soft px-3 py-2 border-t border-line bg-parchment">
            © OpenStreetMap contributors.
          </figcaption>
        </figure>
      ) : (
        <EmptyState location={location} />
      )}
    </div>
  )
}

function EmptyState({ location }: { location: string | null }) {
  return (
    <div className="bg-parchment border border-line rounded-lg p-5 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brass-soft text-brass mb-2">
        <Icon name="map" size={20} />
      </div>
      <div className="font-semibold text-sm mb-1">Station map not available</div>
      <div className="text-xs text-ink-soft max-w-sm mx-auto leading-snug">
        {location
          ? <>We have map images for Pickering, Levisham, Goathland, Grosmont, Grosmont MPD, and Whitby. Set this event’s location to one of those to surface the map here.</>
          : <>Set the event location in event settings to surface a station map here.</>}
      </div>
    </div>
  )
}
